import { orderBy } from 'lodash';

import { IS_NATERA } from '@constants/constants';
import { Actions, AppActions, AppState } from '@customTypes/app';
import { OrganizationRole } from '@customTypes/clinician';
import { ProviderMemberClinic } from '@customTypes/user';
import { formatFullName } from '@helpers/clinician';

const getClinicsWithRole = (clinics: ProviderMemberClinic[], orgRole: OrganizationRole): ProviderMemberClinic[] => {
  const clinicsWithRole = clinics.filter(clinic => clinic.roles.includes(orgRole));
  return orderBy(clinicsWithRole, ['name'], ['asc']);
};

const Reducer = (state: AppState, action: AppActions): AppState => {
  switch (action.type) {
    case Actions.USER_PROFILE_UPDATED: {
      const { campaigns = [], clinics = [] } = action.payload;

      const hasPartnershipOrdering = !IS_NATERA && campaigns.length > 0;

      const adminClinics = getClinicsWithRole(clinics, OrganizationRole.ADMIN);
      const orderingProviderClinics = getClinicsWithRole(clinics, OrganizationRole.CLINICIAN);

      return {
        ...state,
        hasPartnershipOrdering,
        adminClinics,
        orderingProviderClinics,
        userProfile: {
          ...action.payload,
          fullName: formatFullName({
            first: action.payload.first_name,
            last: action.payload.last_name,
            credentials: action.payload.credentials,
          }),
        },
      };
    }

    case 'SET_NEW_REQUISITION':
      return {
        ...state,
        newRequisition: action.payload,
      };

    case 'SET_FORMATTED_PRODUCTS_LIST':
      return {
        ...state,
        formattedProductsList: action.payload,
      };

    case 'SET_REQUISITION_TASK_FORM':
      return {
        ...state,
        requisitionTaskForm: action.payload,
      };

    case 'PATCH_REQUISITION_TASK_FORM':
      return {
        ...state,
        requisitionTaskForm: {
          ...(state.requisitionTaskForm || {}),
          ...action.payload,
        },
      };

    case 'SET_PATIENT_DETAIL':
      return {
        ...state,
        patientsMap: {
          ...state.patientsMap,
          [action.payload.patientId]: action.payload.result,
        },
      };

    case 'SET_CLINICAL_TEAM':
      return {
        ...state,
        clinicalTeam: action.payload,
      };

    case 'ADD_CLINICAL_TEAM_MEMBER':
      return {
        ...state,
        clinicalTeam: state.clinicalTeam.concat(action.payload),
      };

    case 'REMOVE_CLINICAL_TEAM_MEMBER':
      return {
        ...state,
        clinicalTeam: state.clinicalTeam.filter(item => item.clinician_id !== action.payload),
      };

    case 'SET_USERS': {
      const newUsers = action.payload;

      return {
        ...state,
        users: newUsers,
        orgDefaultDelegates: newUsers.filter(user => user.organizational_roles?.includes(OrganizationRole.DELEGATE)),
      };
    }

    case 'UPDATE_USER': {
      const updatedUsers = state.users.map(item =>
        item.clinician_id === action.payload.clinician_id ? action.payload : item,
      );

      return {
        ...state,
        users: updatedUsers,
        orgDefaultDelegates: updatedUsers.filter(user =>
          user.organizational_roles?.includes(OrganizationRole.DELEGATE),
        ),
      };
    }
    default:
      return state;
  }
};

export default Reducer;
